import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'betz-water/password';

interface AuthContextValue {
  password: string | null;
  isAuthenticated: boolean;
  login: (password: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  // Test seam to inject a Storage-shaped object.
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

export function AuthProvider({ children, storage }: AuthProviderProps) {
  const store = storage ?? (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  const [password, setPassword] = useState<string | null>(() => store?.getItem(STORAGE_KEY) ?? null);

  const login = useCallback(
    (next: string) => {
      store?.setItem(STORAGE_KEY, next);
      setPassword(next);
    },
    [store],
  );

  const logout = useCallback(() => {
    store?.removeItem(STORAGE_KEY);
    setPassword(null);
  }, [store]);

  const value = useMemo<AuthContextValue>(
    () => ({ password, isAuthenticated: password !== null, login, logout }),
    [password, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
